import {
	Box,
	Chip,
	ChipPropsVariantOverrides,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Grid,
} from '@mui/material'
import React, { lazy, useState } from 'react'
import { formatLocaleShortDate } from '../../../services/CommonServices/DataFormatters'
import { WorkItem } from '../../../types/Work/WorkItem'
import { OpenInfoDialogButton } from '../../workiteminfo/OpenInfoDialogButton'
import { Dangerous, ExpandLess, ExpandMore } from '@mui/icons-material'
import { ComponentInspectionForm } from './ComponentInspectionForm'
import { ComponentChangeOrder } from '../../../types/Work/Survey'

const OpenAttachmentsButton = lazy(() =>
	import('../../attachments/OpenAttachmentsButton').then((module) => ({
		default: module.OpenAttachmentsButton,
	})),
)
const OpenConstraintsDialogButton = lazy(() =>
	import('../../constraint/ConstraintsDialog').then((module) => ({
		default: module.OpenConstraintsDialogButton,
	})),
)

type SurveyWorkItemRowProps = {
	workItem: WorkItem
	selectAttachments: (workItemId: number) => void
	layer: number
	refreshData: () => Promise<void>
	handleComponentValueChange: (
		component: ComponentChangeOrder,
		newValue: number | null,
		blobs?: Blob[],
	) => Promise<void>
	surveyCompleted: boolean
}

export function SurveyWorkItemRow({
	workItem,
	selectAttachments,
	layer,
	refreshData,
	handleComponentValueChange,
	surveyCompleted,
}: SurveyWorkItemRowProps) {
	const getLayerVariant = (): keyof ChipPropsVariantOverrides => {
		return `Layer${workItem.wbsIndex}${
			workItem.outOfScope ? 'OutOfScope' : 'WorkItem'
		}` as keyof ChipPropsVariantOverrides
	}

	const constraintCount = workItem.constraints?.filter((x) => !x.timeCleared).length ?? 0
	const isLastLayer = !workItem.childrenWorkItems || workItem.childrenWorkItems.length === 0
	const [isExpanded, setIsExpanded] = useState(false)
	return (
		<>
			<TableRow
				key={workItem.id}
				sx={{
					backgroundColor: 'white',
					'& > td': {
						borderBottom:
							isLastLayer && workItem.componentChangeOrders?.length && isExpanded
								? 'none'
								: undefined,
					},
				}}
			>
				<TableCell align='center' sx={{ px: 0 }}>
					<IconButton size='small' onClick={() => setIsExpanded(!isExpanded)}>
						{isExpanded ? <ExpandLess /> : <ExpandMore />}
					</IconButton>
				</TableCell>
				<TableCell align='center' sx={{ px: 0 }}>
					<Chip
						label={workItem.typeName}
						variant={getLayerVariant()}
						sx={{
							marginLeft: `${(layer - 1) * 24}px`,
						}}
					/>
				</TableCell>
				<TableCell align='center' sx={{ px: 0 }}>
					{constraintCount > 0 && (
						<Tooltip title={'Constraint Reported'}>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								<Dangerous color='warning' />({constraintCount})
							</Box>
						</Tooltip>
					)}
				</TableCell>
				<TableCell align='center' sx={{ paddingRight: 0 }}>
					{workItem.outOfScope ? workItem.wbsName : workItem.craftName}
				</TableCell>
				<TableCell align='left' sx={{ paddingRight: 0 }}>
					{workItem.name}
				</TableCell>
				<TableCell align='left' sx={{ paddingRight: 0 }}>
					<OpenInfoDialogButton workItem={workItem} />
				</TableCell>
				<TableCell align='center' sx={{ px: 0 }}>
					{formatLocaleShortDate(workItem.releaseDate)}
				</TableCell>
				<TableCell align='center' sx={{ px: 0 }}>
					<OpenAttachmentsButton
						selectAttachments={() => selectAttachments(workItem.id)}
						attachmentCount={workItem.attachmentCount}
					/>
				</TableCell>
				<TableCell align='center' sx={{ px: 0 }}>
					<OpenConstraintsDialogButton workItem={workItem} loadData={refreshData} />
				</TableCell>
			</TableRow>

			{isLastLayer &&
				isExpanded &&
				workItem.componentChangeOrders &&
				workItem.componentChangeOrders.length > 0 && (
					<TableRow
						sx={{ backgroundColor: 'white' }}
						key={`${workItem.id}-${workItem.componentChangeOrders.length}`}
					>
						<TableCell
							colSpan={9}
							sx={{
								py: 2,
								px: 3,
								borderTop: '1px dashed rgba(224, 224, 224, 1)',
							}}
						>
							<Box
								sx={{
									marginLeft: `${(layer - 1) * 24}px`,
								}}
							>
								<Grid container spacing={2}>
									{workItem.componentChangeOrders.map((component, index) => (
										<Grid item xs={12} sm={6} md={4} key={index}>
											<ComponentInspectionForm
												component={component}
												onValueChange={handleComponentValueChange}
												isEditable={isLastLayer}
												surveyCompleted={surveyCompleted}
											/>
										</Grid>
									))}
								</Grid>
							</Box>
						</TableCell>
					</TableRow>
				)}

			{workItem.childrenWorkItems &&
				isExpanded &&
				workItem.childrenWorkItems.map((child, index) => (
					<SurveyWorkItemRow
						layer={layer + 1}
						workItem={child}
						key={index}
						selectAttachments={selectAttachments}
						refreshData={refreshData}
						handleComponentValueChange={handleComponentValueChange}
						surveyCompleted={surveyCompleted}
					/>
				))}
		</>
	)
}
